/* The console's colour schemes, mirrored.
 *
 * This is a COPY of `PRESETS` and `FIELDS` from ../../../js/theme.js, and the
 * duplication is deliberate rather than an oversight — but it is a debt, so it
 * is written down here rather than left to be discovered.
 *
 * Why copy: the admin app is its own npm project (see admin/README.md), and the
 * console's theme.js is not a data file. It applies colours to `document
 * .documentElement` at import time, reads and writes the salon's settings
 * through prefs.js, and pulls in the whole settings mirror behind it. Importing
 * it across the two projects to get five names and thirty hexes would drag a
 * module that mutates the DOM into a form that only wants to draw swatches.
 *
 * What that costs: a preset added to the console and not here shows up in this
 * app as an unrecognised id. That is handled — `presetOf` falls back and the
 * page says the stored id is unknown rather than silently drawing the default —
 * so the failure is visible rather than wrong. Adding one is two edits.
 *
 * What it does NOT cost: the ability to set a value this file has never heard
 * of. `theme` is jsonb, the Advanced page can write any shape into it, and the
 * console reads the value rather than this list. A preset that exists only in a
 * newer console build can still be set from here through the raw editor.
 */

var PRESETS = [
  {
    id: 'navy', name: 'Navy & Gold', note: 'The original palette — deep navy, warm gold.',
    colors: {
      bg: '#F8F9FA', surface: '#FFFFFF', primary: '#203A5C', accent: '#C5A059',
      text: '#333333', muted: '#6B7480', line: '#E3E7EC', lineSoft: '#EEF1F5',
      blue: '#2E6FD9', danger: '#B3453C', action: '#EDF1F7'
    }
  },
  {
    id: 'slate', name: 'Slate & Teal', note: 'Cooler and greyer, with a teal accent.',
    colors: {
      bg: '#F3F5F7', surface: '#FFFFFF', primary: '#2F3A45', accent: '#3E9E96',
      text: '#2B3138', muted: '#6A7480', line: '#E1E6EA', lineSoft: '#EDF0F3',
      blue: '#2E7FD9', danger: '#B3453C', action: '#E8EDF1'
    }
  },
  {
    id: 'plum', name: 'Plum & Rose', note: 'Warmer, softer, a spa-room palette.',
    colors: {
      bg: '#FAF6FA', surface: '#FFFFFF', primary: '#4A2B4D', accent: '#C07C97',
      text: '#33262F', muted: '#756B72', line: '#EAE0E8', lineSoft: '#F4EDF3',
      blue: '#7A5AD9', danger: '#B3453C', action: '#F1E8F0'
    }
  },
  {
    id: 'forest', name: 'Forest & Brass', note: 'Deep green with a brass accent.',
    colors: {
      bg: '#F4F8F4', surface: '#FFFFFF', primary: '#24453A', accent: '#C0964A',
      text: '#26302A', muted: '#68756D', line: '#DFE8E1', lineSoft: '#EDF3EE',
      blue: '#2E8F6F', danger: '#B3453C', action: '#E7EFE8'
    }
  },
  {
    id: 'ink', name: 'Ink & Amber', note: 'Highest contrast — best on a bright floor.',
    colors: {
      bg: '#ECEEF1', surface: '#FFFFFF', primary: '#1E2124', accent: '#E0A32E',
      text: '#16181B', muted: '#5C646D', line: '#D8DDE3', lineSoft: '#E8ECF0',
      blue: '#1F6FEB', danger: '#A8342B', action: '#DEE3E9'
    }
  }
];

// The six a hex can be typed into. The rest of a preset stays as the preset
// defines it — they are signals rather than branding. Same list, same reasoning
// as the console's.
var FIELDS = [
  { key: 'primary', label: 'Headers & top bar',
    hint: 'The bar across the top and the staff name row. Keep it dark.' },
  { key: 'action', label: 'Quick add buttons',
    hint: 'Fill behind the quick add buttons.' },
  { key: 'accent', label: 'Accent',
    hint: 'Request stars, the half-turn badge, the ten-minute warning.' },
  { key: 'bg', label: 'Page background', hint: 'Behind everything.' },
  { key: 'surface', label: 'Cards & squares', hint: 'The turn squares, panes and modals.' },
  { key: 'text', label: 'Text', hint: 'Body text on light surfaces.' }
];

// Accepts a bare "203a5c" as well, because that is how people paste them.
// Returns null for anything that is not a colour, which callers treat as "leave
// this one to the preset".
function cleanHex(value) {
  if (typeof value !== 'string') return null;
  var v = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(v)) v = v[0] + v[0] + v[1] + v[1] + v[2] + v[2];
  return /^[0-9a-f]{6}$/i.test(v) ? '#' + v.toLowerCase() : null;
}

function presetOf(id) {
  return PRESETS.find(function (p) { return p.id === id; }) || null;
}

// Preset values with any custom hexes laid over the top — what the board would
// actually draw. An unknown preset id resolves against the first preset, which
// is what the console does too ('dream' → 'navy' aside).
function resolve(state) {
  var s = state || {};
  var base = presetOf(s.preset) || PRESETS[0];
  var out = Object.assign({}, base.colors);
  FIELDS.forEach(function (f) {
    var v = cleanHex((s.custom || {})[f.key]);
    if (v) out[f.key] = v;
  });
  return out;
}

export const themePresets = {
  PRESETS: PRESETS,
  FIELDS: FIELDS,
  cleanHex: cleanHex,
  presetOf: presetOf,
  resolve: resolve
};
